import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { stageImage, stagePhotos } from './stage-media'
import { scheduleStageAdvance, stageCanPlay, stageLoopIndex, stageTransitionDuration } from './stage-slideshow'
import './stage-gallery.css'

export default function StageGallery() {
  const [index, setIndex] = useState(0)
  const [request, setRequest] = useState({ index: 0, automatic: false })
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)
  const [playing, setPlaying] = useState(true)
  const [interacting, setInteracting] = useState(false)
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const [readyId, setReadyId] = useState('')
  const [visible, setVisible] = useState(false)
  const [pageVisible, setPageVisible] = useState(true)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [failedId, setFailedId] = useState('')
  const viewport = useRef<HTMLDivElement>(null)
  const thumbnails = useRef<HTMLDivElement>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const expandButton = useRef<HTMLButtonElement>(null)
  const pointer = useRef<{ id: number; x: number; y: number } | null>(null)
  const swiped = useRef(false)
  const playIntent = useRef(true)
  const pointerInput = useRef(false)
  const current = stagePhotos[index] ?? stagePhotos[0]
  const autoplayEligible = stageCanPlay({ playing, visible, pageVisible, expanded, reducedMotion,
    interacting, keyboardFocused, ready: readyId === current.id, pending: false })
  const autoPlaying = autoplayEligible && request.index === index

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncMotion = () => setReducedMotion(preference.matches)
    syncMotion()
    preference.addEventListener('change', syncMotion)
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= .15), { threshold: 0.15 })
    if (viewport.current) observer.observe(viewport.current)
    const syncVisibility = () => setPageVisible(!document.hidden)
    syncVisibility()
    document.addEventListener('visibilitychange', syncVisibility)
    const notePointer = () => { pointerInput.current = true }
    const noteKeyboard = () => { pointerInput.current = false }
    document.addEventListener('pointerdown', notePointer, true)
    document.addEventListener('keydown', noteKeyboard, true)
    return () => { preference.removeEventListener('change', syncMotion); observer.disconnect(); document.removeEventListener('visibilitychange', syncVisibility); document.removeEventListener('pointerdown', notePointer, true); document.removeEventListener('keydown', noteKeyboard, true) }
  }, [])

  useEffect(() => {
    if (!autoPlaying) return
    return scheduleStageAdvance(() => setRequest({ index: stageLoopIndex(index + 1, stagePhotos.length), automatic: true }))
  }, [autoPlaying, index, request])

  // Keep the current photograph visible until the requested one is decoded.
  useEffect(() => {
    if (request.index === index) return
    // A pending automatic change must not replace the image being inspected.
    // Manual arrows and thumbnails still work while autoplay is paused.
    if (request.automatic && !autoplayEligible) {
      setRequest({ index, automatic: false })
      return
    }
    let cancelled = false
    const photo = stagePhotos[request.index]
    const image = new Image()
    const show = (failed = false) => {
      if (cancelled) return
      setPreviousIndex(reducedMotion ? null : index)
      setIndex(request.index)
      setReadyId(photo.id)
      setFailedId(failed ? photo.id : '')
    }
    image.onload = () => { void image.decode().catch(() => {}).then(() => show()) }
    image.onerror = () => show(true)
    image.sizes = '(max-width: 800px) 100vw, 86vw'
    image.srcset = `${stageImage(photo, 640)} 640w, ${stageImage(photo, 1600)} 1600w`
    image.src = stageImage(photo, 1600)
    return () => { cancelled = true; image.onload = null; image.onerror = null }
  }, [request, index, reducedMotion, autoplayEligible])

  useEffect(() => {
    if (previousIndex === null) return
    if (reducedMotion) { setPreviousIndex(null); return }
    const timer = window.setTimeout(() => setPreviousIndex(null), stageTransitionDuration)
    return () => window.clearTimeout(timer)
  }, [previousIndex, index, reducedMotion])

  // Only prepare the next photograph, rather than fetching the entire archive.
  useEffect(() => {
    if (!visible || !pageVisible || expanded) return
    const photo = stagePhotos[stageLoopIndex(index + 1, stagePhotos.length)]
    const image = new Image()
    image.sizes = '(max-width: 800px) 100vw, 86vw'
    image.srcset = `${stageImage(photo, 640)} 640w, ${stageImage(photo, 1600)} 1600w`
    image.src = stageImage(photo, 1600)
  }, [index, visible, pageVisible, expanded])

  useEffect(() => {
    const rail = thumbnails.current
    const button = rail?.children[index] as HTMLElement | undefined
    if (rail && button) rail.scrollTo({ left: button.offsetLeft - (rail.clientWidth - button.clientWidth) / 2, behavior: reducedMotion ? 'instant' : 'smooth' })
  }, [index, reducedMotion])

  useEffect(() => {
    if (!expanded) return
    dialog.current?.showModal()
    closeButton.current?.focus({ preventScroll: true })
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousOverflow; expandButton.current?.focus({ preventScroll: true }) }
  }, [expanded])

  function select(next: number) {
    setRequest({ index: stageLoopIndex(next, stagePhotos.length), automatic: false })
  }

  function move(direction: number) {
    setRequest(previous => ({ index: stageLoopIndex(previous.index + direction, stagePhotos.length), automatic: false }))
  }

  function navigate(event: KeyboardEvent<HTMLElement>, focusThumbnail = false) {
    let next = request.index
    if (event.key === 'ArrowLeft') next = stageLoopIndex(request.index - 1, stagePhotos.length)
    else if (event.key === 'ArrowRight') next = stageLoopIndex(request.index + 1, stagePhotos.length)
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = stagePhotos.length - 1
    else return
    event.preventDefault()
    select(next)
    if (focusThumbnail) (thumbnails.current?.children[next] as HTMLButtonElement | undefined)?.focus({ preventScroll: true })
  }

  function swipeStart(event: PointerEvent<HTMLButtonElement>) {
    if (!event.isPrimary || event.button !== 0) return
    pointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY }
    setInteracting(true)
    swiped.current = false
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  function swipeEnd(event: PointerEvent<HTMLButtonElement>) {
    if (!pointer.current || pointer.current.id !== event.pointerId) return
    const dx = event.clientX - pointer.current.x
    const dy = event.clientY - pointer.current.y
    pointer.current = null
    setInteracting(false)
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.25) {
      swiped.current = true
      move(dx < 0 ? 1 : -1)
    }
  }

  return <section className="stage-gallery section-pad" id="gallery" aria-labelledby="gallery-title">
    <div className="stage-gallery-heading">
      <div><p className="eyebrow">THE WORLD OF WELLNESS / GALLERY</p><h2 id="gallery-title">Every Stage.<br /><em>A New Possibility.</em></h2></div>
      <div className="stage-gallery-intro"><span className="stage-archive-label">WORLD + THAILAND</span><p>สัมผัสบรรยากาศเวทีที่ผ่านมา<br />จากช่วงเวลาบนเวที สู่มิตรภาพเบื้องหลัง</p></div>
    </div>

    <div className="stage-carousel" id="stage-carousel" role="region" aria-roledescription="carousel" aria-label="สไลด์ภาพบรรยากาศ World และ Thailand"
      onPointerDownCapture={() => { pointerInput.current = true; setKeyboardFocused(false) }}
      onKeyDownCapture={() => { pointerInput.current = false; setKeyboardFocused(true) }}
      onFocusCapture={() => { if (!pointerInput.current) setKeyboardFocused(true) }}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setKeyboardFocused(false) }}>
      <div className="stage-carousel-controls"><button type="button" className="stage-play" onPointerDown={() => { playIntent.current = playing }} onClick={event => { setPlaying(event.detail === 0 ? !playing : !playIntent.current); setKeyboardFocused(false) }} disabled={reducedMotion} aria-label={reducedMotion ? 'ปิดสไลด์อัตโนมัติตามการตั้งค่าลดการเคลื่อนไหว' : playing ? 'หยุดสไลด์อัตโนมัติ' : 'เล่นสไลด์อัตโนมัติ'}><span aria-hidden="true">{playing ? 'Ⅱ' : '▷'}</span>{playing ? 'หยุดสไลด์' : 'เล่นสไลด์'}</button></div>
      <div ref={viewport} className="stage-viewport" onKeyDown={event => navigate(event)}>
        <div className="stage-slide" role="group" aria-roledescription="slide" aria-label={`ภาพที่ ${index + 1} จาก ${stagePhotos.length}`}>
          <button type="button" className="stage-image-button" ref={expandButton} aria-label={`ขยายภาพ: ${current.caption}`} aria-haspopup="dialog" onPointerDown={swipeStart} onPointerUp={swipeEnd} onPointerCancel={() => { pointer.current = null; setInteracting(false) }} onLostPointerCapture={() => { pointer.current = null; setInteracting(false) }} onClick={() => { if (swiped.current) { swiped.current = false; return }; setExpanded(true) }}>
            {previousIndex !== null && <img className="stage-main-image stage-main-image--previous" style={{ animationDuration: `${stageTransitionDuration}ms` }} src={stageImage(stagePhotos[previousIndex], 1600)} srcSet={`${stageImage(stagePhotos[previousIndex], 640)} 640w, ${stageImage(stagePhotos[previousIndex], 1600)} 1600w`} sizes="(max-width: 800px) 100vw, 86vw" alt="" aria-hidden="true" draggable="false" />}
            <img key={current.id} className={`stage-main-image${previousIndex !== null ? ' stage-main-image--entering' : ''}`} style={{ animationDuration: `${stageTransitionDuration}ms` }} src={stageImage(current, 1600)} srcSet={`${stageImage(current, 640)} 640w, ${stageImage(current, 1600)} 1600w`} sizes="(max-width: 800px) 100vw, 86vw" alt={current.caption} loading="lazy" decoding="async" draggable="false" onLoad={() => setReadyId(current.id)} onError={() => { setFailedId(current.id); setReadyId(current.id) }} />
            {failedId === current.id && <span className="stage-image-error">โหลดภาพนี้ไม่สำเร็จ กรุณาเลือกภาพถัดไป</span>}
            <span className="stage-expand" aria-hidden="true">ขยายภาพ ↗</span>
          </button>
        </div>
        <button type="button" className="stage-arrow stage-arrow--previous" aria-label="ภาพก่อนหน้า" onClick={() => move(-1)}>←</button>
        <button type="button" className="stage-arrow stage-arrow--next" aria-label="ภาพถัดไป" onClick={() => move(1)}>→</button>
      </div>
      <div className="stage-caption-bar" aria-live={autoPlaying ? 'off' : 'polite'} aria-atomic="true"><p>{current.caption}</p><span className="stage-counter"><strong>{String(index + 1).padStart(2, '0')}</strong><span>/ {String(stagePhotos.length).padStart(2, '0')}</span></span></div>
      <div className="stage-progress" aria-hidden="true"><span style={{ width: `${((index + 1) / stagePhotos.length) * 100}%` }} /></div>
      <div ref={thumbnails} className="stage-thumbnails" role="group" aria-label="เลือกรูปย่อ ใช้ปุ่มลูกศรเพื่อเปลี่ยนภาพ" onKeyDown={event => navigate(event, true)}>{stagePhotos.map((photo, photoIndex) => <button type="button" key={photo.id} className="stage-thumbnail" tabIndex={photoIndex === index ? 0 : -1} aria-label={`ดูภาพที่ ${photoIndex + 1}: ${photo.caption}`} aria-current={photoIndex === index ? 'true' : undefined} onClick={() => select(photoIndex)}><img src={stageImage(photo, 240)} alt="" width="120" height="80" loading="lazy" decoding="async" draggable="false" /><span aria-hidden="true">{String(photoIndex + 1).padStart(2, '0')}</span></button>)}</div>
    </div>
    <p className="stage-archive-note">ภาพบรรยากาศและสื่อจากงาน Miss Wellness World / Thailand ที่ผ่านมา ข้อมูลบนโปสเตอร์และภาพข่าวเป็นข้อมูลของงานนั้น ๆ</p>

    <dialog ref={dialog} className="stage-lightbox" aria-labelledby="stage-lightbox-title" onClose={() => setExpanded(false)} onClick={event => { if (event.target === event.currentTarget) dialog.current?.close() }} onKeyDown={event => navigate(event)}>
      {expanded && <><div className="stage-lightbox-header"><span>WORLD + THAILAND / {index + 1} OF {stagePhotos.length}</span><button type="button" ref={closeButton} aria-label="ปิดภาพขยาย" onClick={() => dialog.current?.close()}>✕</button></div><div className="stage-lightbox-image"><img src={stageImage(current, 1600)} alt={current.caption} decoding="async" /><button type="button" className="stage-arrow stage-arrow--previous" aria-label="ภาพก่อนหน้าแบบขยาย" onClick={() => move(-1)}>←</button><button type="button" className="stage-arrow stage-arrow--next" aria-label="ภาพถัดไปแบบขยาย" onClick={() => move(1)}>→</button></div><p id="stage-lightbox-title">{current.caption}</p></>}
    </dialog>
  </section>
}
